/*
 * The scaffold's own copy, and the home of every locale key the server can answer with that no
 * screen of its own renders. One shipped language, English (Q38): nothing outside an i18n file
 * contains user-facing text, and every area keeps its screens' strings beside its code —
 * `core/shell/`, `core/settings/`, a module's `i18n/en.ts` — which `core/i18n/index.ts` merges into
 * the one typed catalogue `t()` is checked against.
 *
 * Two blocks live here. The first is the frame every screen shares: the application's name, the
 * verbs of a dialogue, the connection banner and the fourteen wire error codes of protocol.md §1.1,
 * so a server error always has something to say even when it sends no key of its own.
 *
 * The second is the server's own vocabulary — `error.rt.*`, `error.file.*`, `error.search.*`,
 * `error.ops.*` and the rest. Every one of them is spelled as it arrives on the wire: a service
 * names its refusal by a bare code and `wire.LocaleKey` puts the `error.` head on it, so a key
 * written the way the Go source spells it would never be found. Those keys arrive at runtime and
 * are rendered through `translate()`, never through a `t("…")` a screen spells out, so they belong
 * to no area's catalogue. The two families that do
 * belong to one are the identity area's (`error.account.*`, `error.auth.*`, `error.policy.*`, in
 * `core/auth/i18n/en.ts`) and the chat module's (`error.chat.*`, its own namespace).
 *
 * A placeholder is written `{name}` and is typed: t() will not compile without it. The server block
 * uses none, on purpose: a refusal reaches a person through several paths and not all of them carry
 * the error's `args` — a refusal that came back through the outbox has lost them — so a `{limit}`
 * there would be shown verbatim. These texts say that a limit was passed, not which.
 */

export const en = {
	"app.name": "teamBase",
	"app.loading": "Loading …",
	"app.skipToContent": "Skip to content",

	"action.cancel": "Cancel",
	"action.close": "Close",
	"action.confirm": "Confirm",
	"action.retry": "Try again",
	"action.save": "Save",
	// The way back from an action that took effect at once, offered in the toast that reports it.
	"action.undo": "Undo",

	// ---- J-02: the two days a date is named by rather than written out ----
	//
	// They belong to core because `format.ts`'s `formatDay()` is the one spelling of a day in the
	// product — the timeline's pill, the jump panel's presets, Activity's headings, a search hit —
	// and core cannot read a module's catalogue. `format.ts` reads these two entries out of this
	// object directly: `t()` lives in `t.ts`, which reads `LOCALE` from `format.ts` at module scope,
	// so importing `t()` back into `format.ts` would be a cycle that throws while the module loads.
	"day.today": "Today",
	"day.yesterday": "Yesterday",

	"connection.connecting": "Connecting …",
	"connection.offline": "No connection. Next attempt in {seconds} s.",
	"connection.catchup": "Catching up on messages …",
	"connection.draining": "The server is restarting. The connection will be back shortly.",
	"connection.revoked": "This session has ended. Please sign in again.",

	// ---- T-07: the one outage a reader can cause themselves, and the last minute ----
	//
	// `connection.rateLimited` is the sentence for close code 4429: twenty connections a minute is
	// what a reload habit reaches (`realtime.connect.acct`), and "No connection. Trying again."
	// blamed the network for it (X-B1 P13). The wait is the backoff's own, counted down live.
	//
	// `day.justNow` is read out of this object by `format.ts` the way the two days above are, and for
	// the same reason — a cycle — so it carries no placeholder either.
	"connection.rateLimited": "Too many connections — trying again in {seconds} s.",
	"day.justNow": "just now",

	// The fourteen wire codes: what a refusal says when it carries no key of its own.
	"error.invalid": "That input could not be processed.",
	"error.unauthenticated": "Please sign in again.",
	"error.forbidden": "You do not have permission for that.",
	"error.stepUpRequired": "For safety, this needs to be confirmed again.",
	"error.csrf": "This request has expired. Please reload the page.",
	"error.notFound": "Not found.",
	"error.conflict": "Something changed in the meantime.",
	"error.gone": "That does not exist any more.",
	"error.tooLarge": "Too large.",
	"error.rateLimited": "Too many attempts. Please wait a moment.",
	"error.unavailable": "Not reachable right now.",
	"error.insufficientStorage": "There is not enough storage space.",
	"error.internal": "Something went wrong.",
	"error.protocol": "The connection was interrupted.",
	"error.network": "The server cannot be reached.",
	"error.unknown": "Unknown error.",

	"storage.refused": "This browser stores nothing locally. Drafts only live in this tab.",

	// ---- the realtime connection and the HTTP frame (internal/core/realtime, internal/core/web) ----
	//
	// Most of these describe a frame the client itself built wrongly, so a person meets them only
	// when two versions disagree. They still get a sentence: a raw key is never shown at a person.
	"error.rt.malformed": "This request could not be read.",
	"error.rt.version": "This request has the wrong protocol version.",
	"error.rt.kind": "This request has an unknown kind.",
	"error.rt.id": "This request is missing its id.",
	"error.rt.data": "This request is missing its data.",
	"error.rt.badId": "This id is not valid.",
	"error.rt.staleId": "This request is out of date.",
	"error.rt.unknownField": "This request contains an unknown field.",
	"error.rt.unknownType": "The server does not know this request type.",
	"error.rt.helloRepeated": "This connection is already signed in.",
	"error.rt.focusInvalid": "This page cannot be followed live.",
	"error.rt.method": "This address expects a different method.",
	"error.rt.subprotocol": "This browser speaks the wrong protocol. Please reload the page.",
	"error.rt.protocolVersion": "A newer version is available. The page will reload.",
	// *Area* is the shell's word for one of its regions, so a module that is switched off cannot be
	// called one; and this block carries no placeholder, so it cannot name the module either (the
	// dispatcher refuses a request type belonging to any disabled module — realtime/hub/conn.go).
	"error.rt.moduleDisabled": "This part of teamBase is switched off.",
	"error.rt.notImplemented": "That does not exist yet.",
	"error.rt.invalidLimit": "This count is not valid.",
	"error.rt.invalidOutcome": "This filter value is not valid.",
	"error.rt.invalidQuery": "This query is not valid.",
	"error.rt.tooLarge": "This request is too large.",
	// The answer rather than the request: what the server would have to tell everybody about this
	// change is larger than one event may be. A module that can say the same thing in a smaller
	// shape does so before anybody meets this — a message whose rendered tree does not fit is sent
	// without it, and the client fetches the tree — so this is the sentence for what is left over.
	"error.rt.eventTooLarge": "This is too large to send. Please shorten it.",
	"error.rt.rateLimited": "Too many requests. Please wait a moment.",
	"error.rt.capacity": "The server is busy. The connection will be back shortly.",
	"error.rt.draining": "The server is restarting. The connection will be back shortly.",
	"error.rt.unavailable": "Not reachable right now.",
	"error.rt.internal": "Something went wrong.",
	// The client's own: a request the socket gave up on at its deadline (core/net/socket.ts). The
	// server never sends it, and the outbox retries what it names.
	"error.rt.timeout": "The server did not answer in time.",

	// The same conditions on the plain HTTP side (internal/core/web). A service names its refusal
	// by a bare code and the wire spells it "error.<code>" (wire.LocaleKey), so the catalogue is
	// written in the spelling that arrives, never in the one the Go source holds.
	"error.rt.badRequest": "This request could not be read.",
	"error.rt.notFound": "Not found.",
	"error.rt.frontendMissing": "The interface is missing on the server. Please tell the team.",

	// ---- documents (internal/core/docs) ----
	// A document someone may not reach answers like one that does not exist (DV S-01 Appendix A).
	"error.doc.notFound": "This document doesn’t exist, or you can’t open it.",
	// Handled by the document's own code and never shown: a malformed request, and a session a tab
	// may not write under — somebody else's, or one a restored server or a lost future retired —
	// after which the tab opens a new session and sends again.
	"error.doc.invalid": "This change could not be read.",
	"error.doc.sessionNotYours": "This change was sent under somebody else’s name.",
	"error.doc.sessionRetired": "This change is sent again.",
	// Markdown over the parser's 4 MiB, from an opened file or a paste, and a paste of foreign HTML
	// past what the parse route reads (DV S-02 Appendix J.2), which the page answers by pasting the
	// plain text.
	"error.doc.markdownTooLarge": "This file is too large to open as a document.",
	"error.doc.mediaTooLarge": "{name} is larger than 100 MB and wasn’t added.",
	// Version history (DV S-05 Appendix D.2; S-01 Appendix A): a version a cut deleted or the history
	// cannot build, a name of no characters or past 100, and a restore or an undo that meets typing,
	// a version too large, or text changed since. A batch that is no server-made change is never
	// offered an undo, so its refusal is the core's and never shown.
	"error.doc.history.notFound": "This version doesn’t exist.",
	"error.doc.history.cut": "This version was deleted.",
	"error.doc.history.unbuildable": "This version can’t be shown right now.",
	// Deleting history (DV S-05 §3.9): a cut with nothing before it, an undo after the seven days, the
	// journal that could not be written. Only the newest deletion is offered an undo, so the refusal of
	// an older one is the core's and never shown.
	"error.doc.history.nothingToDelete": "There’s no history before this to delete.",
	"error.doc.history.cutExpired": "This can’t be undone any more: 7 days have passed.",
	"error.doc.history.unavailable": "History can’t be deleted right now. Try again later.",
	"error.doc.history.undoNotNewest": "Only the newest deletion of history can be undone.",
	"error.doc.version.nameInvalid": "A name needs 1 to 100 characters.",
	"error.doc.restore.busy": "The document is changing fast. Try again in a moment.",
	"error.doc.restore.unbuildable":
		"This version can’t be shown right now, so it can’t be restored.",
	"error.doc.restore.tooLarge": "This version is larger than a document may be.",
	"error.doc.restore.changedSince":
		"Someone changed this text since ({heading}). Restore an earlier version, or edit it by hand.",
	"error.doc.restore.notRevertible": "This change can’t be undone this way.",
	"error.doc.tooLargeAtOnce": "This change is too large to make at once. Make it in smaller parts.",
	// An option list's rules, as Edit options meets them (DV S-17 §3.6): options are only ever added,
	// renamed or hidden, a name belongs to one option, and a document holds at most 256 lists.
	"error.doc.enum.invalid": "This option list can’t be saved as it is.",
	"error.doc.enum.tooMany": "This document has 256 lists. Reuse one, or start a new document.",
	"error.doc.enum.changed":
		"Someone changed this list. Your changes were kept — check and save again.",
	"error.doc.enum.appendOnly":
		"Options are never deleted, only hidden: a hidden option stays wherever it was chosen.",
	"error.doc.enum.movedOption":
		"That name belongs to another option of this list. Choose a different name.",
	"error.doc.enum.duplicate": "Another option already has this name.",
	// A table command (DV S-17 §3.3): the table's rows or columns moved since the page saw it; one
	// that does not apply, whose `reason` names the cell menu's hint the page shows beside it; and one
	// too large to finish within the document's deadline twice in a row.
	"error.doc.table.changed": "The table changed. Try again.",
	"error.doc.table.cannot": "This table can’t be changed that way.",
	"error.doc.table.tooLarge":
		"This table is too large to change at once. Split it into smaller tables.",
	// The tab acts (DV W-2 Appendix E), read by a page that raced another editor or a cap: the first
	// tab, which stays first and is never removed or nested; 30 live tabs; a name of 1 to 100
	// characters; one level of sub-tabs; a tab another editor removed or moved out of reach.
	"error.doc.tabs.first": "The first tab stays first and can’t be removed.",
	"error.doc.tabs.tooMany": "A document holds at most {max} tabs.",
	"error.doc.tabs.nameEmpty": "A tab needs a name.",
	"error.doc.tabs.nameLong": "A tab’s name has at most {max} characters.",
	"error.doc.tabs.depth": "A sub-tab can’t hold sub-tabs.",
	"error.doc.tabs.gone": "That tab isn’t there any more.",
	// A removed tab's history is read-only: a cut or its undo started from its own panel (W2-D13).
	"error.doc.tabs.restoreToCut": "Restore the tab to delete its history.",
	// *Export ▸ Every tab* past four documents' worth of text (W2-D14).
	"error.doc.export.tooLarge":
		"This document is too long to export at once. Export the tabs one at a time.",
	// The document's work could not run now: its actor is busy or stopping, or the one conversion a
	// process runs at a time — a paste of foreign HTML — was taken (DV S-01 Appendix A). A tab sends
	// the same frame again, and a paste goes in as plain text.
	"error.doc.busy": "teamBase is busy. Your changes are kept on this device.",
	// A markdown block's preview asked for with a hash that is not its source's (DV S-16 §3.3): the
	// block keeps showing its source under this line.
	"error.doc.render.hash": "The preview request was damaged. Try again.",
	// A comment's reactions are full — 40 different emoji, or 10 of one person (DV S-04 Appendix B.7).
	// The picker greys out first; this is what a page that raced the cap reads, in the picker's words.
	"error.doc.comment.reactionsFull": "No more reactions here.",
	// A comment's caps, read by a page that raced them (DV S-04 Appendix B.7), and a comment that asked
	// Claude where no agent desk runs (release 1): the comment is kept either way.
	"error.doc.comment.tooMany": "This document has 1 000 comment threads. Delete some to add more.",
	"error.doc.comment.threadFull": "This thread has 100 comments. Start a new one.",
	"error.doc.agent.none": "Claude isn’t available for comments yet. Your comment was kept.",
	// Somebody who opened the document by its link named a person or asked Claude (DV W-1 D25): both
	// are for the people of the document's drive.
	"error.doc.visitor.mention": "Mentions and Send to Claude need a teamBase account.",
	// The name a visitor gives (DV W-1 D17): 1 to 60 characters of text, fixed once it first wrote.
	"error.doc.visitor.name": "A name is 1 to 60 characters.",
	// A document's link was changed while its drive or the instance keeps every link closed (DV W-1
	// §3.5): the dialog redraws disabled with the reason.
	"error.doc.link.closed": "Links outside teamBase are turned off here.",
	// A document's level for AI chats was set while its drive is closed to AI chats (DV A-1 A1-D4): the
	// row in Who can open redraws disabled with the drive's reason, whose words these are (A-4 §2.2).
	"error.doc.agents.closed": "No AI chat can read anything in this drive.",

	// ---- attachments (internal/core/files) ----
	"error.file.notFound": "This attachment does not exist.",
	"error.file.missing": "This file is no longer on the server.",
	"error.file.variantMissing": "This preview does not exist.",
	"error.file.notReady": "This attachment has not finished uploading.",
	// An avatar that is not a picture: refused where the mistake was made (auth/avatar.go), because
	// the alternative is an account drawn with a broken image in every list.
	"error.file.notAPicture": "A profile picture has to be a picture.",
	"error.file.alreadySent": "This attachment already belongs to a message that was sent.",
	"error.file.bindingEmpty": "No attachment was named for this message.",
	"error.file.ownerUnknown": "This attachment has no target.",
	"error.file.wrongScope": "This attachment belongs somewhere else.",
	"error.file.scopeInvalid": "This upload target is not valid.",
	"error.file.scopeUnknown": "This upload target does not exist.",
	"error.file.nameMissing": "This file is missing its name.",
	"error.file.nameInvalid": "This file name does not work.",
	"error.file.nameTooLong": "This file name is too long.",
	"error.file.captionTooLong": "The caption is too long.",
	"error.file.tooLarge": "This file is too large.",
	"error.file.tooManyPixels": "This image has too many pixels.",
	// The same defence counted in memory rather than in pixels: a picture can be inside the pixel cap
	// and still need most of a gigabyte to open, because sixteen bits of colour and the buffer the
	// resampler needs are not in a pixel count (files/config.go, MaxDecodeBytes).
	"error.file.imageTooHeavy": "This image would need too much memory to open.",
	// Bytes that do not parse as the picture they claim to be — a file that was cut short on the way.
	// The stripper walks every image before it is stored (files/exif.go), so this is where a corrupt
	// one is caught, and it is the sender's file that is wrong.
	"error.file.imageInvalid": "This image file is damaged. Please try it again or send another.",
	"error.file.tooManyPerMessage": "That is more attachments than one message can carry.",
	"error.file.tooManyInFlight": "Too many uploads at once. Please wait a moment.",
	"error.file.lengthRequired": "This upload is missing its size.",
	"error.file.uploadIdMissing": "This upload is missing its id.",
	"error.file.uploadIdReused": "This upload id is already taken.",
	"error.file.resumeNotSupported": "This upload cannot be resumed. Please start it again.",
	"error.file.skipped": "This part of the upload was skipped.",
	"error.file.bodyUnreadable": "The file could not be read.",
	"error.file.scanPending": "This file is still being checked. Try again in a moment.",
	"error.file.scanInfected": "This file contains malware and was not accepted.",
	"error.file.quotaExceeded": "This instance has used up its storage space.",
	"error.file.diskFull": "There is no space left on the server. Please tell the team.",
	"error.file.storeAnomaly": "The file store did not answer as expected.",

	// ---- search (internal/core/search) ----
	"error.search.queryTooLong": "The search term is too long.",
	"error.search.tooManyTerms": "Too many search terms at once.",
	"error.search.tooManyFilters": "Too many filters in one search.",
	// The cap is on what the *caller* can see, not on what they asked for (search/service.go's
	// visibleScopes), and "area" belongs to the shell's regions — so the sentence says which it is.
	"error.search.tooManyScopes": "You are in too many conversations for one search.",
	"error.search.limitTooLarge": "Too many results were asked for at once.",
	"error.search.modeUnknown": "This way of searching does not exist.",
	"error.search.cursorInvalid": "This page of results is out of date. Please search again.",
	"error.search.kindUnknown": "This kind of result can’t be searched here.",
	"error.search.busy": "Search is busy right now. Please try again in a moment.",

	// ---- searching the repositories (internal/core/search/code) ----
	// The search screen draws these as the sentence of the section that failed, from its own
	// catalogue, because the other two sections answered and a toast over all three would say that
	// the whole search is broken. These are the same sentences for every other caller and for a
	// client this build does not know the screen of.
	"error.search.code.patternInvalid": "That cannot be searched for.",
	"error.search.code.unknownRepo": "No project here is called that.",
	"error.search.code.cursorInvalid": "This page of results is out of date. Please search again.",
	"error.search.code.busy": "Too many searches at once. Try again in a moment.",
	"error.search.code.unavailable": "The repositories cannot be searched right now.",
	// The code file page's two refusals: a file that is gone, excluded or on a commit the branch does
	// not hold (one sentence for all of them, so the page says nothing about what a clone keeps), and
	// a file that is not text.
	"error.search.codeFileGone": "This file is not in the project any more, or it cannot be shown.",
	"error.search.codeFileBinary": "This file is not text, so it cannot be shown here.",

	// ---- operations (internal/core/ops, internal/core/logging) ----
	"error.ops.doctor.unavailable": "The system check is not available.",
	"error.ops.backup.unavailable": "Backups are not set up on this instance.",
	"error.ops.backup.kind": "This kind of backup does not exist.",
	"error.ops.backup.locked": "A backup is already running.",
	"error.ops.backup.failed": "The backup failed.",
	"error.ops.debug.unavailable": "The verbose log is not available.",
	"error.ops.debug.minutes": "This duration is outside the allowed range.",
	"error.ops.debug.minutesInvalid": "This duration is not a valid number.",

	// ---- instance and interface settings (internal/core/settings) ----
	"error.settings.unknownKey": "This setting does not exist.",
	"error.settings.badValue": "This value does not fit this setting.",
	"error.settings.versionConflict": "This setting changed in the meantime. Please reload.",
	"error.settings.reasonLength": "The reason is too long.",
	"error.ui.unknownKey": "This interface setting does not exist.",
	"error.ui.badValue": "This value does not fit this interface setting.",
	"error.ui.tooManyKeys": "Too many interface settings at once.",

	// ---- the activity feed (internal/core/activity) ----
	"error.activity.unknownKind": "Activity does not know this kind of item.",
	"error.activity.badFilter": "Activity has no such filter.",
	"error.activity.badSelection": "This selection does not work.",
	"error.activity.tooManyIds": "Too many items at once.",
	"error.activity.dataTooBig": "This item is too large.",
	"error.activity.noRecipient": "This item is missing its recipient.",

	// ---- notifications (internal/core/notify) ----
	"error.notify.badPolicy": "This notification rule does not exist.",
	"error.notify.badDelay": "This waiting time does not work.",
	"error.notify.badValue": "This value does not fit this notification setting.",

	// ---- mail (internal/core/mailer), met in the administration's mail table ----
	"error.mail.notFound": "This e-mail does not exist.",
	"error.mail.notRetryable": "This e-mail cannot be sent again.",
	"error.mail.unknownKind": "This kind of e-mail does not exist.",
	"error.mail.unknownState": "An e-mail has no such state.",
	"error.mail.unknownLocale": "There is no template for this language.",
	"error.mail.wrongData": "The data does not fit this kind of e-mail.",
	"error.mail.dataTooLarge": "This e-mail carries too much data.",
	"error.mail.invalidAddress": "This does not look like an e-mail address.",

	// ---- background jobs (internal/core/jobs) ----
	"error.jobs.unknownJob": "This job does not exist.",
	"error.jobs.alreadyRunning": "This job is already running.",
} as const satisfies Record<string, string>
